import React, { useState } from 'react'
import tw from 'twin.macro'

import { Button } from 'common/components/Button/Button'
import { Input } from 'common/components/Input/Input'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useGetIsUserAdminQuery } from 'modules/Users/userApi'
import { useDisconnectStripeConnectMutation } from 'modules/ProductInfo/productInfoApi'
import {
  getStripeConnectExchangeErrorReason,
  getStripeDisconnectErrorMessage,
} from 'modules/ProductInfo/utils/stripeConnect'

export const DISCONNECT_STRIPE_BUTTON_LABEL = 'Disconnect Stripe'
export const DISCONNECT_STRIPE_CONFIRM_LABEL = 'Yes, disconnect'
export const DISCONNECT_STRIPE_CANCEL_LABEL = 'Keep connected'
export const DISCONNECT_STRIPE_CONFIRM_TEXT =
  'Trybe will no longer be able to manage products, prices or coupons in your Stripe account, and your ' +
  'checkout page will stop working until you connect an account again. Existing subscribers will not be able ' +
  'to cancel or manage their subscription in the app until the same Stripe account is connected again. ' +
  'Payments you have already received stay in your Stripe account.'
export const DISCONNECT_STRIPE_CONFIRM_WORD = 'DISCONNECT'
export const DISCONNECT_STRIPE_CONFIRM_INPUT_LABEL = `Type ${DISCONNECT_STRIPE_CONFIRM_WORD} to confirm`
export const STRIPE_DISCONNECTED_TEXT = 'Your Stripe account has been disconnected.'

/**
 * Lets a Trybe admin unlink the org's Stripe account from the Sell setup page.
 *
 * Only Trybe admins see it for now, because a coach disconnecting by accident
 * breaks their checkout. Clicking the button first shows what disconnecting
 * means, and the confirm button stays disabled until the admin types
 * DISCONNECT. Confirming calls the `disconnectStripeConnect` callable, which
 * revokes Trybe's access on Stripe's side and clears the org's account id and
 * publishable key. The page's live listener on that key then swaps this
 * section for the Connect button on its own; this component only reports the
 * outcome.
 */
export function DisconnectStripe({ coachOrgId }) {
  const { userId } = useAuth()
  const { data: isTrybeAdmin } = useGetIsUserAdminQuery({ userId })
  const { createAlert } = useAlert()
  const [isConfirming, setIsConfirming] = useState(false)
  const [confirmWord, setConfirmWord] = useState('')
  const [disconnectStripeConnect, { isLoading }] = useDisconnectStripeConnectMutation()

  // Entries under internal/admins are not all the boolean true; some are an
  // object or a string. Treat any truthy entry as an admin, the same way the
  // Trybe Admin tab in AccountNavbar does, so the two never disagree.
  if (!isTrybeAdmin) return null

  const closeConfirmation = () => {
    setIsConfirming(false)
    setConfirmWord('')
  }

  const disconnect = async () => {
    const { error } = (await disconnectStripeConnect({ coachOrgId })) || {}
    if (error) {
      console.error(error)
      createAlert({ text: getStripeDisconnectErrorMessage(getStripeConnectExchangeErrorReason(error)), type: 'danger' })
      return
    }
    closeConfirmation()
    createAlert({ text: STRIPE_DISCONNECTED_TEXT, type: 'success' })
  }

  return (
    <div className='mt-6 flex flex-col items-start' data-testid='disconnect-stripe'>
      {isConfirming ? (
        <div className='w-full bg-white rounded-xl p-6 border-2 border-gray-200'>
          <p className='text-gray-500 mb-4'>{DISCONNECT_STRIPE_CONFIRM_TEXT}</p>
          <label htmlFor='disconnect-stripe-confirm' className='block text-sm text-gray-500 mb-2'>
            Type <span className='font-semibold text-tBlack'>{DISCONNECT_STRIPE_CONFIRM_WORD}</span> to confirm
          </label>
          <Input
            name='disconnect-stripe-confirm'
            autoComplete='off'
            value={confirmWord}
            onChange={(e) => setConfirmWord(e.target.value)}
            disabled={isLoading}
            css={tw`h-10 py-2 mb-4 max-w-[14rem] rounded-lg`}
          />
          <div className='flex items-center'>
            <Button variant='secondary' size='md' css={tw`mr-2`} onClick={closeConfirmation} disabled={isLoading}>
              {DISCONNECT_STRIPE_CANCEL_LABEL}
            </Button>
            <Button
              variant='danger'
              size='md'
              loading={isLoading}
              disabled={confirmWord !== DISCONNECT_STRIPE_CONFIRM_WORD}
              onClick={disconnect}
            >
              {DISCONNECT_STRIPE_CONFIRM_LABEL}
            </Button>
          </div>
        </div>
      ) : (
        <button
          type='button'
          className='text-sm text-gray-400 hover:text-tRed underline underline-offset-2 transition-colors'
          onClick={() => setIsConfirming(true)}
          disabled={!coachOrgId}
        >
          {DISCONNECT_STRIPE_BUTTON_LABEL}
        </button>
      )}
    </div>
  )
}
